import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Copy, CheckCircle2, Lock, CreditCard, Building2 } from "lucide-react";
import api from "../lib/api";
import { capture, identify } from "../lib/analytics";

export default function Enrol() {
  const { token } = useParams();
  const [status, setStatus] = useState("checking"); // checking | ready | submitting | done | invalid
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    session_preference: "tue_am",
    notes: "",
    payment_preference: "bank_transfer",
  });

  useEffect(() => {
    api.get(`/public/enrolment/${token}`)
      .then((r) => { setInfo(r.data); setStatus("ready"); })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      const { data } = await api.post(`/public/enrolment/${token}/submit`, form);
      if (form.email) identify(form.email, { name: form.name });
      capture("enrolment_submitted", { session_preference: form.session_preference || "none" });
      setResult(data);
      setStatus("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
      setStatus("ready");
    }
  };

  if (status === "checking") return <CenterShell>Checking your invitation…</CenterShell>;
  if (status === "invalid") return (
    <CenterShell>
      <Lock size={42} className="mx-auto text-[#A685BD] mb-3" />
      <h1 className="font-display text-4xl text-[#5D2C8F]">This link isn't valid</h1>
      <p className="text-[#5A4A6B] mt-3">The enrolment link may have expired or been revoked. Please ask your tutor to send you a fresh one.</p>
      <Link to="/" className="sac-btn-secondary mt-8 inline-flex" data-testid="enrol-home-btn">Back to home</Link>
    </CenterShell>
  );

  return (
    <div className="min-h-screen py-12" data-testid="enrol-page">
      <Toaster position="top-center" />
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-8">
          <div className="font-hand text-[#A685BD] text-2xl">private enrolment</div>
          <h1 className="font-display text-4xl md:text-5xl text-[#5D2C8F] mt-1 brush-underline">You're invited to join</h1>
          <p className="text-sm text-[#6E6074] mt-4">
            Complete the form below to reserve your seat for <strong>{info?.current_term || "the current term"}</strong>.
            Your payment details will be shown once you submit.
          </p>
        </div>

        {status !== "done" && (
          <form onSubmit={submit} className="soft-card p-8 space-y-4" data-testid="enrol-form">
            <Row>
              <Field label="Your full name" required value={form.name} onChange={(v)=>setForm({...form,name:v})} testid="enrol-name" />
              <Field label="Email" type="email" required value={form.email} onChange={(v)=>setForm({...form,email:v})} testid="enrol-email" />
            </Row>
            <Row>
              <Field label="Phone" value={form.phone} onChange={(v)=>setForm({...form,phone:v})} testid="enrol-phone" />
              <div>
                <Label>Preferred session*</Label>
                <select required value={form.session_preference} onChange={(e)=>setForm({...form, session_preference: e.target.value})}
                  className="mt-1 w-full bg-white border border-[#E8E2D5] rounded-xl px-4 py-3" data-testid="enrol-session">
                  <option value="tue_am">Tuesday AM (10am – 1pm)</option>
                  <option value="tue_pm">Tuesday PM (2pm – 5pm)</option>
                  <option value="fri_eve">Friday Evening (6pm – 8:30pm)</option>
                </select>
              </div>
            </Row>
            <div>
              <Label>Any notes for us</Label>
              <textarea rows={3} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}
                placeholder="Any painting experience? Anything we should know?"
                className="mt-1 w-full bg-white border border-[#E8E2D5] rounded-xl px-4 py-3" data-testid="enrol-notes" />
            </div>
            <div>
              <Label>Preferred payment</Label>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <PayOpt active={form.payment_preference==="bank_transfer"} onClick={()=>setForm({...form, payment_preference: "bank_transfer"})} icon={<Building2 size={16} />} label="Bank transfer" sub="Preferred · fee-free" testid="pay-bank" />
                <PayOpt active={form.payment_preference==="card"} onClick={()=>setForm({...form, payment_preference: "card"})} icon={<CreditCard size={16} />} label="Card" sub="On request" testid="pay-card" />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-[#6E6074]">Course fee: <strong>£{info?.course_price ?? 405}</strong> · 14-week term</span>
              <button type="submit" disabled={status==="submitting"} className="sac-btn-primary" data-testid="enrol-submit-btn">
                {status==="submitting" ? "Submitting…" : "Reserve my seat"}
              </button>
            </div>
          </form>
        )}

        {status === "done" && result && (
          <div className="soft-card p-8" data-testid="enrol-done">
            <div className="text-center">
              <CheckCircle2 size={42} className="mx-auto text-[#8EC9C7] mb-3" />
              <h2 className="font-display text-3xl text-[#5D2C8F]">You're in — welcome aboard!</h2>
              <p className="text-[#5A4A6B] mt-3">Your place for <strong>{result.term}</strong> is reserved. Below are the bank details to complete your £{result.price} payment.</p>
            </div>

            <div className="mt-8 bg-[#F4EDDD] rounded-2xl p-6">
              <div className="font-hand text-[#A685BD] text-xl">pay by bank transfer</div>
              <h3 className="font-display text-2xl text-[#5D2C8F] mt-1">Our bank details</h3>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <BankField label="Account holder" value={result.bank.account_holder} testid="bank-holder" />
                <BankField label="Institution" value={result.bank.institution} testid="bank-institution" />
                <BankField label="Sort code" value={result.bank.sort_code} testid="bank-sort" />
                <BankField label="Account number" value={result.bank.account_number} testid="bank-account" />
              </div>
              <p className="text-xs text-[#6E6074] mt-4">
                Please use your full name as the payment reference so we can match it to your enrolment.
              </p>
            </div>

            <div className="mt-5 bg-white border border-[#E8E2D5] rounded-2xl p-5 flex items-start gap-3">
              <CreditCard size={18} className="text-[#5D2C8F] mt-0.5" />
              <div className="text-sm text-[#5A4A6B]">
                <strong className="text-[#5D2C8F]">Prefer card?</strong> {result.card_note}
              </div>
            </div>

            <p className="text-xs text-[#6E6074] mt-6 text-center">
              Questions? Email <a className="underline" href="mailto:seasonsartclasscroydon@gmail.com">seasonsartclasscroydon@gmail.com</a> — we'll also send a welcome pack once payment arrives.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ children }) { return <div className="grid md:grid-cols-2 gap-4">{children}</div>; }
function Label({ children }) { return <label className="text-sm text-[#5D2C8F] font-medium">{children}</label>; }
function Field({ label, required, type="text", value, onChange, testid }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-[#A685BD]">*</span>}</Label>
      <input data-testid={testid} required={required} type={type} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white border border-[#E8E2D5] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5D2C8F]" />
    </div>
  );
}
function PayOpt({ active, onClick, icon, label, sub, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-left rounded-xl border px-4 py-3 transition-colors ${active ? "border-[#5D2C8F] bg-[#5D2C8F]/5" : "border-[#E8E2D5] hover:border-[#8A6DA3]"}`}>
      <div className="flex items-center gap-2 text-[#5D2C8F] font-medium">{icon} {label}</div>
      <div className="text-xs text-[#6E6074] mt-1">{sub}</div>
    </button>
  );
}
function BankField({ label, value, testid }) {
  const copy = () => { navigator.clipboard.writeText(String(value)); toast.success("Copied"); };
  return (
    <div className="bg-white rounded-xl px-4 py-3 border border-[#E8E2D5]">
      <div className="text-xs uppercase tracking-widest text-[#6E6074]">{label}</div>
      <div className="flex items-center justify-between gap-2 mt-1">
        <div className="font-display text-[#5D2C8F] text-base break-all" data-testid={testid}>{value}</div>
        <button onClick={copy} className="text-[#5D2C8F] p-1 hover:bg-[#F4EDDD] rounded" title="Copy"><Copy size={14} /></button>
      </div>
    </div>
  );
}
function CenterShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <div className="max-w-lg">{children}</div>
    </div>
  );
}
